import React from 'react';
import styled from 'styled-components';
import { Lock } from 'lucide-react';
import currency from 'helpers/currency';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent } from 'theme/panelAccent';

const MASK = '••••';

/**
 * No lugar do painel de vendas (Ingressos e Produtos) para quem colabora no
 * evento sem `event.sales.view`: mostra o que o evento vende (nome e preço) e
 * esconde os números, que a API nem manda. Os valores aparecem como ••••.
 *
 * @param {{ items: Array, copy: { soldLabel: string, sectionTitle: string, emptyText: string } }} props
 */
export default function RestrictedSalesPanel({ items = [], copy }) {
    return (
        <Wrap>
            <Notice role='status'>
                <Lock size={14} color={panelAccent(colors.secondaryPurple)} aria-hidden='true' />
                Os valores de venda estão ocultos: o organizador não liberou essa visão para você.
            </Notice>

            <Summary>
                <Card>
                    <CardLabel>{copy.soldLabel}</CardLabel>
                    <Masked aria-label='valor oculto'>{MASK}</Masked>
                </Card>
                <Card>
                    <CardLabel>Disponíveis</CardLabel>
                    <Masked aria-label='valor oculto'>{MASK}</Masked>
                </Card>
                <Card>
                    <CardLabel>Receita total</CardLabel>
                    <Masked aria-label='valor oculto'>R$ {MASK}</Masked>
                </Card>
            </Summary>

            <Section>
                <SectionTitle>{copy.sectionTitle}</SectionTitle>

                {items.length === 0 ? (
                    <Empty>{copy.emptyText}</Empty>
                ) : (
                    <List>
                        {items.map((item) => (
                            <Row key={item.id}>
                                <ItemInfo>
                                    <ItemName>{item.name}</ItemName>
                                    {item.price != null && <ItemPrice>{currency.format(item.price)}</ItemPrice>}
                                </ItemInfo>

                                <ItemNumbers>
                                    <span>Vendidos <Masked as='strong' aria-label='valor oculto'>{MASK}</Masked></span>
                                    <span>Arrecadado <Masked as='strong' aria-label='valor oculto'>R$ {MASK}</Masked></span>
                                </ItemNumbers>
                            </Row>
                        ))}
                    </List>
                )}
            </Section>
        </Wrap>
    );
}

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    padding-bottom: 32px;
`;

const Notice = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 10px;
    background: ${colors.neutral};
    border: 1px dashed ${colors.nightLight};
    color: ${colors.night};
    font-size: 13px;
`;

const Summary = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 16px;
    border-radius: 14px;
    background: ${colors.neutralLight};
    border: 1px solid rgba(0, 0, 0, 0.07);
`;

const CardLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;

const Masked = styled.span`
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: ${colors.nightLight};
    user-select: none;

    strong& {
        font-size: inherit;
    }
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
    border-radius: 14px;
    background: ${colors.neutralLight};
    border: 1px solid rgba(0, 0, 0, 0.07);
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Empty = styled.p`
    margin: 0;
    font-size: ${fonts.sizes.default};
    color: ${colors.night};
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
`;

const Row = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 0;
    border-top: 1px solid ${colors.neutralDark};

    &:first-child {
        border-top: 0;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const ItemInfo = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const ItemName = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ItemPrice = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;

const ItemNumbers = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};

    strong {
        font-size: ${fonts.sizes.default};
        color: ${colors.nightLight};
        letter-spacing: 0.08em;
    }
`;
